import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import SEO from '../../components/SEO';
import heroImage from '../../assets/exterior-hero.png';

const Exterior = () => {
    const benefits = [
        "Weather-Proof Protection",
        "Anti-Algal & Anti-Fungal Treatment",
        "Long-lasting Gloss/Matt Finish",
        "Crack Bridging Technology",
        "UV Protection",
        "Waterproof Exterior Coating"
    ];

    const fadeInUp = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6 }
    };

    return (
        <div className="min-h-screen pb-20">
            <SEO 
                title="Exterior Painting Services Madurai" 
                description="Protect your home from the harsh sun and rain with our expert exterior painting in Madurai." 
            />
            
            {/* Hero Section */}
            <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src={heroImage} alt="Exterior Painting" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-blue-900/70"></div>
                </div>
                <div className="relative z-10 text-center px-4">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-6xl font-bold text-white mb-4"
                    >
                        Exterior Painting Services
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-xl text-blue-100 italic"
                    >
                        "Shielding your home with style and strength."
                    </motion.p>
                </div>
            </section>

            <div className="max-w-4xl mx-auto px-4 mt-20">
                <motion.div {...fadeInUp} className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-slate-100 mb-16">
                    <h2 className="text-3xl font-bold text-blue-900 mb-6">Expert Exterior Solutions in Madurai</h2>
                    <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                        Madurai's tropical weather can be tough on your building's exterior. We specialize in applying high-performance exterior paints that not only look stunning but also protect your walls from extreme heat and heavy monsoon rains.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {benefits.map((benefit, i) => (
                            <motion.div 
                                key={i} 
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="flex items-center space-x-3 p-4 bg-orange-50 border border-orange-100 rounded-2xl"
                            >
                                <ShieldCheck className="text-blue-900 flex-shrink-0" />
                                <span className="font-bold text-gray-800">{benefit}</span>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                <motion.div {...fadeInUp} className="bg-blue-900 text-white p-12 rounded-[3rem] shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/20 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700"></div>
                    <h3 className="text-2xl font-bold mb-6 text-orange-500">Why Exterior Painting Matters</h3>
                    <p className="text-blue-50 leading-relaxed text-lg relative z-10">
                        A good exterior paint job is the first line of defense for your structure. It prevents moisture from seeping into the walls, reduces heat absorption, and significantly increases the resale value of your property. Our team uses specialized tools and safety equipment to ensure a perfect finish even for multi-story buildings.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default Exterior;
